import type {AnchorPosition, PositionSettings} from '@primer/behaviors'
import {getAnchoredPosition} from '@primer/behaviors'
import {useProvidedRefOrCreate, useResizeObserver} from '@primer/react'
import {useCallback, useLayoutEffect, useState} from 'react'

const lastDelimiterRegex = /:|,|\s(?=(?:[^"]*"[^"]*")*[^"]*$)/g

export interface AnchoredPositionHookSettings extends Partial<PositionSettings> {
  floatingElementRef?: React.RefObject<Element | null>
  caretElementRef?: React.RefObject<Element | null>
  inputElementRef: React.RefObject<HTMLInputElement | null>
}

/**
 * Calculates the top and left values for an absolutely-positioned floating element
 * to be anchored to some anchor element. Returns refs for the floating element
 * and the anchor element, along with the position.
 * @param settings Settings for calculating the anchored position.
 * @param dependencies Dependencies to determine when to re-calculate the position.
 * @returns An object of {top: number, left: number} to absolutely-position the
 * floating element.
 */
export function useDelimiterAnchorPosition(
  settings?: AnchoredPositionHookSettings,
  dependencies: React.DependencyList = [],
): {
  floatingElementRef: React.RefObject<Element | null>
  caretElementRef: React.RefObject<Element | null>
  position: AnchorPosition | undefined
  updatePosition: () => void
} {
  const floatingElementRef = useProvidedRefOrCreate(settings?.floatingElementRef)
  const caretElementRef = useProvidedRefOrCreate(settings?.caretElementRef)
  const inputElementRef = useProvidedRefOrCreate(settings?.inputElementRef)
  const [position, setPosition] = useState<AnchorPosition | undefined>(undefined)

  const closestDelimiter = () => {
    const inputElement = inputElementRef.current
    if (!inputElement) return null
    const delimiters = Array.from(inputElement.closest('.styled-input-container')?.querySelectorAll('.delimiter') ?? [])

    const filter = inputElement.value
    if (!filter || !inputElement.selectionStart) return null
    const beforeCursorStr = filter.slice(0, inputElement.selectionStart)
    const matches = [...beforeCursorStr.matchAll(lastDelimiterRegex)]

    if (matches.length < 1) return inputElement
    // Verify valid delimiter
    let match
    for (let i = matches.length ?? 0; i > 0; i--) {
      const delimiter = delimiters?.[i - 1]
      if (delimiter && !delimiter?.classList.contains('text-delimiter')) {
        match = delimiter
        break
      }
    }

    return match ?? inputElement
  }

  const updatePosition = useCallback(
    () => {
      const delimiter = closestDelimiter() ?? inputElementRef.current
      if (floatingElementRef.current instanceof Element && delimiter instanceof Element) {
        setPosition(getAnchoredPosition(floatingElementRef.current, delimiter, settings))
      } else {
        setPosition(undefined)
      }
    },
    // This is intentional as it was causing too many calculations to be made
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [floatingElementRef, caretElementRef, ...dependencies],
  )

  useLayoutEffect(updatePosition, [updatePosition])

  useResizeObserver(updatePosition)

  return {
    floatingElementRef,
    caretElementRef,
    position,
    updatePosition,
  }
}
